import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import dayjs from 'dayjs'
import { Timestamp } from 'firebase/firestore'
import {
  BsBell,
  BsBook,
  BsChatDots,
  BsLightningCharge,
  BsMusicNoteBeamed,
  BsStars,
} from 'react-icons/bs'
import { IoClose } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'

import {
  RedesignNotification,
  RedesignNotificationType,
} from './notificationTypes'
import { useUser } from '../../../context'
import { useNotifications } from '../../../hooks/useNotifications'
import challengeIllustration from '../../assets/notifications/challenge.png'
import chatIllustration from '../../assets/notifications/chat.png'
import courseIllustration from '../../assets/notifications/course.png'
import exerciseIllustration from '../../assets/notifications/exercise.png'
import routineIllustration from '../../assets/notifications/routine.png'
import { RoleTone, theme } from '../../constants'

import {
  Badge,
  EmptyNotifications,
  NotificationHeader,
  NotificationHeaderCopy,
  NotificationItem,
  NotificationList,
  NotificationPanel,
  PopoverAnchor,
  RoundButton,
} from './styles'

const icons = {
  challenge_created: BsStars,
  challenge_updated: BsStars,
  exercise_added: BsMusicNoteBeamed,
  course_opened: BsBook,
  routine_updated: BsLightningCharge,
  chat_message: BsChatDots,
}

const illustrations: Partial<Record<RedesignNotificationType, string>> = {
  challenge_created: challengeIllustration,
  challenge_updated: challengeIllustration,
  exercise_added: exerciseIllustration,
  course_opened: courseIllustration,
  routine_updated: routineIllustration,
  chat_message: chatIllustration,
}

const destinations: Record<RedesignNotificationType, string> = {
  challenge_created: '/desafios',
  challenge_updated: '/desafios',
  exercise_added: '/rutina',
  course_opened: '/ruta-formacion',
  routine_updated: '/rutina',
  chat_message: '/chat',
}

interface NotificationCenterProps {
  role?: RoleTone
}

const NotificationCenter = ({ role = 'student' }: NotificationCenterProps) => {
  const navigate = useNavigate()
  const { user, userRole } = useUser()
  const roleColor = theme.roleColors[role]
  const { feed, unreadCount, lastSeen, markAllAsSeen } = useNotifications({
    userId: user?.uid,
    userRole,
    pageSize: 20,
  })
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  const notifications = useMemo(
    () =>
      feed.slice(0, 8).map(
        (item): RedesignNotification => ({
          ...item,
          type: item.type as RedesignNotificationType,
        }),
      ),
    [feed],
  )

  const close = useCallback((returnFocus = true) => {
    setOpen(false)
    if (returnFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) close(false)
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
        return
      }

      if (event.key !== 'Tab' || !panelRef.current) return

      const dialog = panelRef.current.querySelector('[role="dialog"]')
      if (!dialog) return

      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      )
      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close])

  useEffect(() => {
    if (open && unreadCount > 0) markAllAsSeen()
  }, [open, unreadCount, markAllAsSeen])

  useEffect(() => {
    if (open) closeButtonRef.current?.focus()
  }, [open])

  const isUnread = (createdAt?: Timestamp) => {
    if (!createdAt) return false
    if (!lastSeen) return true
    return createdAt.toMillis() > lastSeen.toMillis()
  }

  const openNotification = (notification: RedesignNotification) => {
    close(false)
    navigate(destinations[notification.type])
  }

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault()
      setOpen(true)
    }
  }

  return (
    <PopoverAnchor ref={panelRef}>
      <RoundButton
        ref={triggerRef}
        type="button"
        aria-label={`Notificaciones${
          unreadCount ? `, ${unreadCount} nuevas` : ''
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
        $role={role}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={handleTriggerKeyDown}
      >
        <BsBell aria-hidden="true" />
        {unreadCount > 0 && (
          <Badge $role={role} aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </RoundButton>

      {open && (
        <NotificationPanel
          $role={role}
          role="dialog"
          aria-modal="false"
          aria-labelledby="redesign-notifications-title"
        >
          <NotificationHeader>
            <NotificationHeaderCopy>
              <h2 id="redesign-notifications-title">Notificaciones</h2>
              <span>
                {unreadCount === 0 ? 'Todo al día' : `${unreadCount} sin leer`}
              </span>
            </NotificationHeaderCopy>
            <RoundButton
              ref={closeButtonRef}
              type="button"
              aria-label="Cerrar notificaciones"
              $role={role}
              onClick={() => close()}
            >
              <IoClose aria-hidden="true" />
            </RoundButton>
          </NotificationHeader>

          {notifications.length === 0 ? (
            <EmptyNotifications aria-live="polite">
              Estás al día. No hay notificaciones nuevas.
            </EmptyNotifications>
          ) : (
            <NotificationList>
              {notifications.map((notification) => {
                const Icon = icons[notification.type] || BsBell
                const illustration = illustrations[notification.type]

                return (
                  <NotificationItem
                    key={notification.id}
                    type="button"
                    $unread={isUnread(notification.createdAt)}
                    $role={role}
                    style={
                      {
                        '--notification-role-main': roleColor.main,
                        '--notification-role-soft': roleColor.soft,
                        '--notification-role-strong': roleColor.strong,
                        '--notification-role-text': roleColor.text,
                        '--notification-role-focus': roleColor.focus,
                      } as React.CSSProperties
                    }
                    onClick={() => openNotification(notification)}
                  >
                    <span className="illustration">
                      {illustration ? (
                        <img src={illustration} alt="" aria-hidden="true" />
                      ) : (
                        <Icon aria-hidden="true" />
                      )}
                    </span>
                    <div className="content">
                      <div className="row">
                        <strong>{notification.title}</strong>
                        {notification.createdAt && (
                          <time>
                            {dayjs(notification.createdAt.toDate()).format(
                              'DD/MM',
                            )}
                          </time>
                        )}
                      </div>
                      {notification.body && <p>{notification.body}</p>}
                    </div>
                  </NotificationItem>
                )
              })}
            </NotificationList>
          )}
        </NotificationPanel>
      )}
    </PopoverAnchor>
  )
}

export default NotificationCenter
